import { Component, Inject, LOCALE_ID, OnDestroy } from '@angular/core';
import { formatNumber } from '@angular/common';
import { Observable, Subscription, Subject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  GridReadyEvent,
  IDatasource,
  GridOptions,
  ValueFormatterParams
} from 'ag-grid-community';
import {
  InfiniteScrollingService,
  stringComparer,
  numberComparer,
  ComparatorMap,
  roleComparer,
  ReusableStreamProperty
} from '@ipreo/muni-shared';
import { ManagersRetailService } from '../../../services/managers-retail.service';
import { NegativeCasesService } from '../../../../gameday/services/negative-cases.service';
import { getSortedColumns } from '../../../mappers/reports.calculations';
import { ColumnDefinition } from '../../../../models/column';
import { ManagerRetailReport } from '../../../models/manager';

@Component({
  providers: [ManagersRetailService],
  selector: 'muni-gameday-managers-retail-table',
  templateUrl: './managers-retail.template.html',
  styleUrls: ['./managers-retail.styles.scss'],
  standalone: false
})
export class ManagersRetailComponent implements OnDestroy {
  private readonly gridReadySubj = new Subject<GridReadyEvent>();
  private readonly columnDefs: ColumnDefinition[] = [
    {
      headerName: 'Manager',
      headerClass: 'manager',
      field: 'name',
      position: 1,
      comparator: stringComparer,
      suppressHeaderMenuButton: true
    },
    {
      headerName: 'Retail - Other',
      field: 'other',
      position: 48,
      comparator: numberComparer,
      type: 'numericColumn',
      suppressHeaderMenuButton: true,
      valueFormatter: ({ value }: ValueFormatterParams) =>
        formatNumber(value / 1000 || 0, this.locale)
    },
    {
      headerName: 'Retail - Total',
      field: 'total',
      position: 49,
      sort: 'desc',
      comparator: numberComparer,
      type: 'numericColumn',
      suppressHeaderMenuButton: true,
      valueFormatter: ({ value }: ValueFormatterParams) =>
        formatNumber(value / 1000 || 0, this.locale)
    },
    {
      headerName: 'Manager Type',
      field: 'role',
      position: 50,
      comparator: roleComparer,
      type: 'numericColumn',
      suppressHeaderMenuButton: true
    }
  ];

  public readonly gridOptions: GridOptions = {
    onGridReady: (params: GridReadyEvent) => {
      this.gridReadySubj.next(params);
    },
    autoSizeStrategy: { type: 'fitGridWidth' },
    onGridSizeChanged: ({ api }) => api.sizeColumnsToFit()
  };

  public columns = this.managersRetailService.getColumns;
  public managersTotalObject =
    this.managersRetailService.getManagersRetailTotalList;
  public noCategoryColumnHeaderLabel =
    this.managersRetailService.noCategoryColumnHeaderLabel;
  public isColumnsPresent = this.managersRetailService.isColumnsPresent;

  public totals$: Observable<ManagerRetailReport> =
    this.managersTotalObject.pipe(
      map((total) => ({
        name: 'Total',
        other: total.otherTotal,
        ...total
      }))
    );

  public readonly columnDefs$: Observable<ColumnDefinition[]> =
    this.columns.pipe(
      map((state) => {
        return [].concat(this.columnDefs).concat(
          state.map((column) => ({
            headerName: column,
            field: column,
            position: this.columnDefs.length,
            type: 'numericColumn',
            suppressHeaderMenuButton: true,
            comparator: numberComparer,
            valueFormatter: ({ value }: ValueFormatterParams) =>
              formatNumber(value / 1000 || 0, this.locale)
          }))
        );
      }),
      map((columns) => getSortedColumns(columns, 'position'))
    );

  @ReusableStreamProperty
  private readonly grid$: Observable<
    [
      GridReadyEvent,
      ManagerRetailReport[],
      ManagerRetailReport,
      ColumnDefinition[]
    ]
  > = combineLatest(
    this.gridReadySubj,
    this.managersRetailService.getRetailOrdersData,
    this.totals$,
    this.columnDefs$
  );
  private readonly gridSubscribtion: Subscription = this.grid$.subscribe(
    ([{ api }, managerRetail, totals, allColumnDefs]) => {
      const sortMap: ComparatorMap[] = allColumnDefs
        .map(({ field, comparator }: ColumnDefinition) => ({
          colId: field,
          comparator: comparator
            ? (a: any, b: any) => (comparator as any)(a, b)
            : undefined
        }))
        .filter((item) => item.comparator);
      const datasource: IDatasource =
        this.infiniteScrollingService.getDataSource(
          managerRetail,
          undefined,
          sortMap
        );
      api.setDatasource(datasource);
      api.setGridOption('pinnedTopRowData', [totals]);
    }
  );

  @ReusableStreamProperty
  public isLowPriorityColumnPresent = combineLatest(
    this.managersTotalObject,
    this.isColumnsPresent
  ).pipe(
    map(([totals, isColumns]) => totals.lowPriorityTotal !== 0 && !isColumns)
  );

  @ReusableStreamProperty
  public isOtherColumnPresent = combineLatest(
    this.managersRetailService.isNoCategoryColumnPresent,
    this.isColumnsPresent
  ).pipe(map(([isNoCategory, isColumns]) => isNoCategory && isColumns));

  @ReusableStreamProperty
  public isNoCategoryColumnPresent = combineLatest(
    this.managersRetailService.isNoCategoryColumnPresent,
    this.isColumnsPresent
  ).pipe(map(([isNoCategory, isColumns]) => isNoCategory && !isColumns));

  @ReusableStreamProperty
  public emptyMessage = this.negativeCasesService.messages.pipe(
    map((messages) => messages.managers)
  );

  constructor(
    private managersRetailService: ManagersRetailService,
    private negativeCasesService: NegativeCasesService,
    private infiniteScrollingService: InfiniteScrollingService,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  public ngOnDestroy(): void {
    this.gridSubscribtion.unsubscribe();
  }
}
